// src/components/FeedbackWidget.jsx
import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { apiSendFeedback } from '../services/api';
import styles from './FeedbackWidget.module.css';

const LOW_RATING_OPTIONS = [
  'Bills were extracted incorrectly',
  'Bank matching was inaccurate',
  'Ledger mapping was inaccurate',
  'Review & Confirm Dashboard is not up to mark',
  'App was slow or timed out',
  'Confusing to use',
  'Missing a feature I needed',
  'Other',
];

const HIGH_RATING_OPTIONS = [
  'Saves me time',
  'Accurate extraction',
  'Review & Confirm Dashboard is amazing',
  'Easy to use',
  'Good bank matching',
  'Reliable Tally exports',
  'Other',
];

// Options that need elaboration reveal a shared free-text box
const NEEDS_TEXT = ['Missing a feature I needed', 'Other'];

export default function FeedbackWidget({ onClose }) {
  const { user } = useAuth();
  const [step, setStep] = useState(1);
  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [selectedOptions, setSelectedOptions] = useState([]);
  const [otherText, setOtherText] = useState('');

  const isHighRating = rating >= 4;
  const options = isHighRating ? HIGH_RATING_OPTIONS : LOW_RATING_OPTIONS;
  const showTextBox = selectedOptions.some(opt => NEEDS_TEXT.includes(opt));

  const handleStarClick = (selectedRating) => {
    setRating(selectedRating);
    setStep(2);
  };

  const toggleOption = (option) => {
    setSelectedOptions(prev =>
      prev.includes(option) ? prev.filter(o => o !== option) : [...prev, option]
    );
  };

  const submitFeedback = async (options_ = selectedOptions, text_ = otherText) => {
    const payload = {
      app_name: "CA App (4252)",
      username: user?.username || 'Unknown User',
      email: user?.email || 'Unknown Email',
      rating: rating,
      selected_options: options_,
      other_text: text_,
    };

    try {
      await apiSendFeedback(payload);
    } catch (error) {
      console.error("Silent feedback failure:", error);
    }

    if (isHighRating) {
      setStep(3);
    } else {
      setStep(4);
      localStorage.setItem('ca_feedback_status', 'completed');
      setTimeout(() => onClose('completed'), 2000);
    }
  };

  const handleSkip = () => {
    if (isHighRating) {
      setStep(3);
    } else {
      submitFeedback([], '');
    }
  };

  const handleShare = async () => {
    const shareText = "A web app to create standard customized Tally ready CSV in 2 minutes using AI just by uploading bills and bank statement instead of typing it out for 25 mins.\n\nGive it a try: caaap.systemone.com";

    try {
      if (navigator.share) {
        await navigator.share({
          title: 'Delefy',
          text: shareText,
        });
      } else {
        const waUrl = `https://wa.me/?text=${encodeURIComponent(shareText)}`;
        window.open(waUrl, '_blank', 'noopener,noreferrer');
      }
    } catch (err) {
      console.log('Share canceled or failed', err);
    } finally {
      localStorage.setItem('ca_feedback_status', 'completed');
      onClose('completed');
    }
  };

  return (
    <div className={styles.modalOverlay} onClick={() => onClose('snooze')}>
      <div className={styles.feedbackCard} onClick={(e) => e.stopPropagation()}>

        <button className={styles.closeBtn} onClick={() => onClose('snooze')} type="button">
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
            <line x1="1" y1="1" x2="13" y2="13" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
            <line x1="13" y1="1" x2="1" y2="13" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
          </svg>
        </button>

        {step === 1 && (
          <div className={styles.stepContainer}>
            <h3 className={styles.title}>How was your experience?</h3>
            <p className={styles.subtitle}>Tap a star to rate Delefy</p>
            <div className={styles.starsContainer}>
              {[1, 2, 3, 4, 5].map((star) => (
                <button
                  key={star}
                  type="button"
                  className={styles.starBtn}
                  onClick={() => handleStarClick(star)}
                  onMouseEnter={() => setHoverRating(star)}
                  onMouseLeave={() => setHoverRating(0)}
                >
                  <svg className={`${styles.starIcon} ${(hoverRating || rating) >= star ? styles.starFilled : ''}`} viewBox="0 0 24 24">
                    <path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z" />
                  </svg>
                </button>
              ))}
            </div>
          </div>
        )}

        {step === 2 && (
          <div className={styles.stepContainer}>
            <h3 className={styles.title}>
              {isHighRating ? "Awesome! What do you like most?" : "What went wrong?"}
            </h3>
            <p className={styles.subtitle}>
              {isHighRating ? "Select all that apply — helps us double down on what works." : "Select all that apply — we want to fix it."}
            </p>

            <div className={styles.optionsList}>
              {options.map((opt) => (
                <label key={opt} className={styles.optionRow}>
                  <input
                    type="checkbox"
                    className={styles.optionCheckbox}
                    checked={selectedOptions.includes(opt)}
                    onChange={() => toggleOption(opt)}
                  />
                  <span className={styles.optionLabel}>{opt}</span>
                </label>
              ))}
            </div>

            {showTextBox && (
              <textarea
                className={styles.textarea}
                placeholder="Tell us more..."
                value={otherText}
                onChange={(e) => setOtherText(e.target.value)}
              />
            )}

            <div className={styles.actionRow}>
              <button className={styles.skipBtn} onClick={handleSkip} type="button">Skip</button>
              <button
                className={styles.submitBtn}
                onClick={() => submitFeedback()}
                disabled={selectedOptions.length === 0}
                type="button"
              >
                Submit
              </button>
            </div>
          </div>
        )}

        {step === 3 && (
          <div className={styles.stepContainer}>
            <h3 className={styles.title}>Thank you! 🎉</h3>
            <p className={styles.subtitle}>Know another CA who hates manual data entry? Share this with them.</p>
            <button className={styles.shareBtn} onClick={handleShare} type="button">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                 <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51a12.8 12.8 0 0 0-.57-.01c-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413Z"/>
              </svg>
              Share on WhatsApp
            </button>
          </div>
        )}

        {step === 4 && (
          <div className={styles.stepContainer}>
            <h3 className={styles.title}>Thanks for your honesty.</h3>
            <p className={styles.subtitle}>We're working hard to fix these issues.</p>
          </div>
        )}

      </div>
    </div>
  );
}